import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/DataSheets.scss';

const ELEMENTS = [
  {
    key:              'ownership',
    elementKey:       'ownership',
    label:            'Ownership',
    templateFilename: 'Ownership Data Sheet Template.xlsx',
    scorecardPath:    '/ownership/scorecard',
  },
  {
    key:              'management_control',
    elementKey:       'management_control',
    label:            'Management Control',
    templateFilename: 'Management Control Data Sheet Template.xlsx',
    scorecardPath:    '/management/scorecard',
  },
  {
    key:              'employment_equity',
    elementKey:       'employment_equity',
    label:            'Employment Equity',
    templateFilename: 'Employment Equity Data Sheet Template Road Freight.xlsx',
    scorecardPath:    '/management/scorecard/employment-equity',
  },
  {
    key:              'management_control_transport',
    elementKey:       'management_control_transport',
    label:            'Management Control (Transport)',
    templateFilename: 'Employment Equity Data Sheet Template Road Freight.xlsx',
    scorecardPath:    '/management/scorecard/management-control-transport',
  },
  {
    key:              'skills_development',
    elementKey:       'skills_development',
    label:            'Skills Development',
    templateFilename: 'Skills Development Data Sheet Template.xlsx',
    scorecardPath:    '/skills-development/scorecard',
  },
  {
    key:              'skills_development_transport',
    elementKey:       'skills_development_transport',
    label:            'Skills Development (Transport)',
    templateFilename: 'Road Freight Skills Development Data Sheet Template.xlsx',
    scorecardPath:    '/skills-development/scorecard',
  },
  {
    key:              'skills_development_specialised',
    elementKey:       'skills_development_specialised',
    label:            'Skills Development Specialised',
    templateFilename: 'Skills Development Data Sheet Template.xlsx',
    scorecardPath:    '/skills-development/scorecard',
  },
  {
    key:              'preferential_procurement',
    elementKey:       'procurement',
    label:            'Preferential Procurement',
    templateFilename: 'Procurement Data Sheet Template.xlsx',
    scorecardPath:    '/esd/preferential-procurement/scorecard',
  },
  {
    key:              'enterprise_development',
    elementKey:       'enterprise_development',
    label:            'Enterprise Development',
    templateFilename: 'Enterprise Development Data Sheet Template.xlsx',
    scorecardPath:    '/esd/enterprise-supplier-development/scorecard',
  },
  {
    key:              'enterprise_development_transport',
    elementKey:       'enterprise_development_transport',
    label:            'Enterprise Development (Transport)',
    templateFilename: 'ED Data Sheet Template Road Freight.xlsx',
    scorecardPath:    '/esd/enterprise-supplier-development/scorecard/enterprise-development-transport',
  },
  {
    key:              'socio_economic_development',
    elementKey:       'sed',
    label:            'Socio-Economic Development',
    templateFilename: 'Socio-Economic Development Data Sheet Template.xlsx',
    scorecardPath:    '/sed/scorecard',
  },
  {
    key:              'socio_economic_development_transport',
    elementKey:       'sed_transport',
    label:            'Socio-Economic Development (Transport)',
    templateFilename: 'Road Freight SED Data Sheet Template.xlsx',
    scorecardPath:    '/sed/scorecard/sed-transport',
  },
];

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const DataSheetSection = ({ config }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/core/scorecard/get_scorecard/?element=${config.elementKey}`)
      .then(res => setResult(res.data))
      .catch(() => {});
  }, [config.elementKey]);

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get(`/core/scorecard/download_template/?element=${config.elementKey}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = config.templateFilename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Template not found on server.');
    }
  };

  const handleDownloadDataSheet = async () => {
    try {
      const res = await api.get(`/core/scorecard/download_data_sheet/?element=${config.elementKey}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = result?.filename || config.templateFilename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('No uploaded data sheet found.');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('element', config.elementKey);
      const res = await api.post('/core/scorecard/calculate/', formData);
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 422) {
        setError('No EAP targets found for your province. Update your province in Company Information.');
      } else {
        setError(err.response?.data?.error || 'Failed to process the data sheet. Check the file format and try again.');
      }
    } finally {
      setUploading(false);
      fileInputRef.current.value = '';
    }
  };

    return (
    <div className="ds-section">
      <h2 className="ds-section-title">{config.label}</h2>

      <div className="od-grid">
        <button className="od-card" onClick={handleDownloadTemplate}>
          <div className="od-card-icon"><DownloadIcon /></div>
          <span className="od-card-label">Download Template</span>
          <span className="od-card-desc">{config.label} Data Sheet (.xlsx)</span>
        </button>

        <button
          className={`od-card ${result ? 'od-card--uploaded' : ''}`}
          onClick={() => fileInputRef.current.click()}
          disabled={uploading}
        >
          <div className="od-card-icon"><UploadIcon /></div>
          <span className="od-card-label">
            {uploading ? 'Calculating…' : 'Upload Completed Sheet'}
          </span>
          {result && <span className="od-card-status">✓ {result.filename}</span>}
        </button>

        {result && (
          <button className="od-card od-card--uploaded" onClick={handleDownloadDataSheet}>
            <div className="od-card-icon"><DownloadIcon /></div>
            <span className="od-card-label">Download Uploaded Sheet</span>
            <span className="od-card-desc">{result.filename}</span>
          </button>
        )}
      </div>

      <input
        type="file"
        ref={fileInputRef}
        accept=".xlsx"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {error && (
        <div className="od-error">
          {error}
          {error.includes('province') && (
            <button className="od-error-link" onClick={() => navigate('/finance-statutory/company-information')}>
              Update Company Information
            </button>
          )}
        </div>
      )}

      {result && (
        <div className="od-result">
          <div className="od-result-header">
            <strong>Calculation complete</strong>
            <span>{result.filename}</span>
          </div>
          <div className="od-result-score">
            <span className="od-result-points">{result.total_points}</span>
            <span className="od-result-max">pts</span>
          </div>
          <button className="od-result-btn" onClick={() => navigate(config.scorecardPath)}>
            View Full Scorecard →
          </button>
        </div>
      )}
    </div>
  );
};

const AllDataSheets = () => {
  const navigate = useNavigate();
  const [availableElements, setAvailableElements] = useState(null);

  useEffect(() => {
    api.get('/core/scorecard/list_elements/')
      .then(res => {
        const keys = new Set(res.data.flatMap(group => group.elements));
        setAvailableElements(keys);
      })
      .catch(() => setAvailableElements(null));
  }, []);

  const visibleElements = availableElements
    ? ELEMENTS.filter(el => availableElements.has(el.elementKey))
    : ELEMENTS;

  return (
    <div className="od-container">
      <div className="od-header">
        <button className="od-back" onClick={() => navigate('/')}>← Back</button>
        <div>
          <h1>Data Sheets</h1>
          <p>Download templates, upload completed sheets &amp; calculate your scorecard</p>
        </div>
      </div>

      {visibleElements.map(el => (
        <DataSheetSection key={el.key} config={el} />
      ))}
    </div>
  );
};

export default AllDataSheets;
